// icdlfrontend-app/src/utils/edxSession.ts

const SSO_URL = process.env.SSO_URL ?? 'https://lms.vmb.edu.vn';
const API_URL = process.env.REACT_APP_API_URL ?? '';
const USER_INFO = 'authenticated_user';

/** True nếu OpenEdX session cookie còn hợp lệ. Fail-open: lỗi mạng → true (không gián đoạn bài). */
export async function checkEdxSession(): Promise<boolean> {
  try {
    const res = await fetch(`${SSO_URL}/api/user/v1/me`, { credentials: 'include' });
    return res.status === 200;
  } catch {
    return true; // lỗi network ≠ session hết hạn
  }
}

/** Path + query hiện tại — dùng làm `return_to` để quay lại đúng chỗ sau autologin. */
function currentReturnPath(): string {
  return window.location.pathname + window.location.search;
}

/**
 * Tạo OpenEdX session bằng top-level navigate (KHÔNG dùng hidden iframe).
 *
 * Trước đây dùng hidden iframe để load `redirect_url` — nhưng cookie LMS set trong
 * iframe cross-site bị Chrome chặn bởi SameSite=Lax / third-party cookie policy, nên
 * OpenEdX session không bao giờ thực sự được tạo. Top-level navigation là cách duy
 * nhất set/gửi cookie LMS tin cậy — cùng cơ chế `return_to` mà K12 autologin
 * (backend k12_openedx_entry_views.py) đang dùng ổn định để bounce về đúng FE path.
 *
 * Fire-and-forget — không có gì để chờ vì trang sẽ unload khi OpenEdX login xong.
 */
export function triggerSilentOpenEdxLogin(accessToken: string): Promise<void> {
  const returnTo = encodeURIComponent(currentReturnPath());
  return fetch(`${API_URL}/api/openedx-ott/?return_to=${returnTo}`, {
    headers: { Authorization: `Bearer ${accessToken}`, Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
    .then((data: { redirect_url?: string }) => {
      if (data?.redirect_url) {
        window.location.href = data.redirect_url;
      }
    })
    .catch(() => {
      // silent fail — không block login eduxmaster
    });
}

/**
 * Thử khôi phục OpenEdX session bằng top-level navigate (bounce qua LMS rồi quay lại
 * đúng trang hiện tại qua `return_to`) — xem `triggerSilentOpenEdxLogin` để biết lý do
 * không dùng hidden iframe được nữa.
 *
 * Returns false nếu JWT eduxmaster hết hạn hoặc OTT request lỗi, để caller redirect
 * sang /user/login. Khi OTT request thành công, trang unload ngay sau đó nên promise
 * cố tình không resolve — tránh caller chạy tiếp logic dựa trên state cũ.
 */
export function silentEdxRelogin(): Promise<boolean> {
  const raw = localStorage.getItem(USER_INFO);
  const user = raw ? (JSON.parse(raw) as { access_token?: string }) : null;
  if (!user?.access_token) return Promise.resolve(false);

  const returnTo = encodeURIComponent(currentReturnPath());
  return fetch(`${API_URL}/api/openedx-ott/?return_to=${returnTo}`, {
    headers: { Authorization: `Bearer ${user.access_token}`, Accept: 'application/json' },
  })
    .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
    .then((data: { redirect_url?: string }) => {
      if (!data?.redirect_url) return false;
      window.location.href = data.redirect_url;
      return new Promise<boolean>(() => {
        // trang sắp unload — không resolve
      });
    })
    .catch(() => false);
}

/**
 * Đảm bảo OpenEdX session sẵn sàng TRƯỚC khi load xblock/H5P.
 * Trả false khi không khôi phục được (thường JWT eduxmaster hết hạn).
 */
export async function ensureEdxSession(): Promise<boolean> {
  if (await checkEdxSession()) return true;
  return silentEdxRelogin();
}
